'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import { SignupData } from './AuthPageClient';

interface Step1Data {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface SignupStep1Props {
  onNext: (data: Pick<SignupData, 'name' | 'email' | 'password'>) => void;
  onSwitchToLogin: () => void;
}

export default function SignupStep1({ onNext, onSwitchToLogin }: SignupStep1Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Step1Data>();

  const password = watch('password');

  const onSubmit = (data: Step1Data) => {
    onNext({ name: data.name, email: data.email, password: data.password });
  };

  const handleGoogleSignup = () => {
    // Backend: trigger Google OAuth signup via NextAuth signIn('google')
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-8">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-700">1</div>
          <span className="text-sm font-600 text-primary">Account</span>
        </div>
        <div className="flex-1 h-px bg-border" />
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-600">2</div>
          <span className="text-sm text-muted-foreground">Preferences</span>
        </div>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-700 text-foreground mb-2">Create your account</h1>
        <p className="text-muted-foreground text-sm">Start your journey with Rafeeq today</p>
      </div>

      {/* Google OAuth */}
      <button
        type="button"
        onClick={handleGoogleSignup}
        className="w-full flex items-center justify-center gap-3 border border-border bg-card hover:bg-muted rounded-xl px-4 py-3 text-sm font-500 text-foreground transition-all duration-150 active:scale-95 mb-5"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
          <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
          <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
        </svg>
        Sign up with Google
      </button>

      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-500">or with email</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Name */}
        <div>
          <label htmlFor="signup-name" className="block text-sm font-500 text-foreground mb-1.5">
            Full name
          </label>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            placeholder="Ahmed Hassan"
            className={`w-full bg-muted border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150 ${
              errors.name ? 'border-red-400 bg-red-50' : 'border-border'
            }`}
            {...register('name', { required: 'Your name is required' })}
          />
          {errors.name && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              {errors.name.message}
            </p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="signup-email" className="block text-sm font-500 text-foreground mb-1.5">
            Email address
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="ahmed@example.com"
            className={`w-full bg-muted border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150 ${
              errors.email ? 'border-red-400 bg-red-50' : 'border-border'
            }`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+$/i, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="signup-password" className="block text-sm font-500 text-foreground mb-1.5">
            Password
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters with at least one number</p>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Create a strong password"
              className={`w-full bg-muted border rounded-xl px-4 py-3 pr-12 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150 ${
                errors.password ? 'border-red-400 bg-red-50' : 'border-border'
              }`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'At least 8 characters required' },
                pattern: { value: /\d/, message: 'Must include at least one number' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors duration-150"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="signup-confirm" className="block text-sm font-500 text-foreground mb-1.5">
            Confirm password
          </label>
          <div className="relative">
            <input
              id="signup-confirm"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Repeat your password"
              className={`w-full bg-muted border rounded-xl px-4 py-3 pr-12 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150 ${
                errors.confirmPassword ? 'border-red-400 bg-red-50' : 'border-border'
              }`}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (val) => val === password || 'Passwords do not match',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors duration-150"
              aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
            >
              <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={14} />
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          className="w-full gradient-teal text-white rounded-xl px-4 py-3.5 text-sm font-600 hover:opacity-90 active:scale-95 transition-all duration-150 flex items-center justify-center gap-2"
          style={{ minHeight: '48px' }}
        >
          Continue to Preferences
          <Icon name="ArrowRightIcon" size={18} />
        </button>
      </form>

      <p className="text-center text-xs text-muted-foreground mt-5">
        By creating an account, you agree to our{' '}
        <button className="text-primary font-500 hover:opacity-80 transition-opacity duration-150">Terms of Service</button>{' '}
        and{' '}
        <button className="text-primary font-500 hover:opacity-80 transition-opacity duration-150">Privacy Policy</button>
      </p>

      <p className="text-center text-sm text-muted-foreground mt-4">
        Already have an account?{' '}
        <button
          onClick={onSwitchToLogin}
          className="text-primary font-600 hover:opacity-80 transition-opacity duration-150"
        >
          Sign in
        </button>
      </p>
    </div>
  );
}